import React, { useEffect, useState, useContext } from "react";
import t from "Utils/translation";
import s from "./prices.scss";
import PricesTable from "@/components/pricesTable/PricesTable";
import CustomTierPricesTable from "@/components/pricesTable/CustomTierPricesTable";
import PricesOverview from "@/components/pricesOverview/PricesOverview";
import QboxmailApi from "@/qboxmailApi";
import Loader from "Components/loader/Loader";
import TranslatedString from "Components/TranslatedString";
import Button from "Components/button/Button";
import { useParams } from "react-router-dom";
import useLocalStorage from "Utils/useLocalStorage";
import { view } from "@risingstack/react-easy-state";
import { AuthContext } from "@/App";
import cx from "classnames";
import AdditionalServices, { serviceType } from "../components/additionalServices/AdditionalServices";

const api = new QboxmailApi({ useCookie: true });

const showWHMCSInvoice = async () => {
  let win = window.open("about:blank", "_blank");
  win.focus();

  try {
    const response = await api.generateWHMCSUrl("get_invoices");
    win.location.replace(response.resources[0].url);
  } catch (response) {
    console.log("error generating whmcs invoice", response);
  }
};

const generateCTAMessage = (upgrade_status, requested) => {
  let r = {
    desc: null,
    action: "prices.request",
  };

  if (requested) {
    r.desc = "prices.ongoingUpgrade";
  } else if (upgrade_status === "less_than_10") {
    r.desc = "prices.lessThan10";
  } else if (upgrade_status === "not_ready") {
    r.desc = "prices.ctaNotReady";
  } else if (upgrade_status === "can_upgrade") {
    r.desc = "prices.upgrade";
  } else if (upgrade_status === "can_upgrade_to_not_standard") {
    r.desc = "prices.upgradeNotStandard";
    r.action = "prices.contactUs";
  }

  return r;
};

const PageSection = ({ className, sectionTitle, sectionDesc, sectionContent }) => {
  return (
    <div className={cx("flex flex-col lg:flex-row gap-8", className)}>
      <div className="flex flex-col lg:w-64">
        <div className="settingTitle">{sectionTitle}</div>

        <div
          className="sectionDesc"
          dangerouslySetInnerHTML={{
            __html: sectionDesc,
          }}
        ></div>
      </div>

      <div className="flex flex-col flex-1">{sectionContent}</div>
    </div>
  );
};

const PricePage = () => {
  const [loading, setLoading] = useState(true);
  const [warningType, setWarningType] = useState(false);
  const [errored, setErrored] = useState(false);
  const { getLoggedUser } = useContext(AuthContext);
  const loggedUser = getLoggedUser();

  const [tier, setTier] = useState(null);

  const [requestedLocalStorage, setRequestedLocalStorage] = useLocalStorage(
    `qb_requested_upgrade_${loggedUser.code}`,
    null,
  );

  const { customerCode } = useParams();

  const getServiceType = () => {
    const customer = loggedUser.type === "Admin" ? tier.customer : loggedUser;

    const trial_or_unpaid = customer.plan === "trial" || customer.status_detail == "wait_pay";

    if (trial_or_unpaid && customer.declared_type === "company") {
      return serviceType.TRIAL_ENDUSER;
    }

    if (trial_or_unpaid && customer.declared_type === "reseller") {
      return serviceType.TRIAL_RESELLER;
    }

    if (trial_or_unpaid) {
      return serviceType.TRIAL_RESELLER;
    }

    if (customer.plan === "paid" && !customer.is_reseller) {
      return serviceType.PAID_ENDUSER;
    }

    return serviceType.PAID_RESELLER;
  };
  useEffect(
    () => {
      setLoading(true);

      api
        .getCustomerTierPrices(customerCode)
        .then((fetchedTier) => {
          if (!fetchedTier) return;
          setTier(fetchedTier);

          if (
            fetchedTier.upgrade_status === "not_ready" ||
            (fetchedTier.active_tier &&
              fetchedTier.active_tier.name != (requestedLocalStorage && requestedLocalStorage.fromPlan))
          ) {
            setRequestedLocalStorage(null);
          }

          setWarningType(
            fetchedTier.lower_than_min_accounts
              ? "lower_than_min"
              : fetchedTier.is_rold
                ? "is_rold"
                : fetchedTier.is_rn
                  ? "is_rn"
                  : false,
          );

          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setErrored(true);
        });
    },
    //eslint-disable-next-line
    [],
  );

  const requestUpgrade = () => {
    setRequestedLocalStorage({
      date: new Date(),
      fromPlan: tier.active_tier.name,
    });
    //eslint-disable-next-line
    api.requestTierUpgrade().catch((err) => {});
  };

  if (errored) {
    return (
      <div
        className={s.loader}
        style={{ paddingTop: "5rem", display: "flex", justifyContent: "center", textAlign: "center" }}
      >
        {t("alert.wrong.contactUs")}
      </div>
    );
  }

  if (loading || !tier) {
    return (
      <div className={cx(s.loader, "pt-32")}>
        <Loader />
      </div>
    );
  }

  const currentService = getServiceType();

  const getAdditionalServicesPrices = () => {
    if (currentService === serviceType.TRIAL_ENDUSER) {
      return {
        archiveMonthly: tier.monthly_tier.archive,
        archiveAnnually: tier.annually_tier.archive,
        additionalServices: tier.additional_services,
      };
    }

    if (currentService === serviceType.TRIAL_RESELLER) {
      return { archiveMonthly: tier.monthly_tier.archive };
    }

    if (currentService === serviceType.PAID_ENDUSER) {
      return {
        [tier.is_annual ? "archiveAnnually" : "archiveMonthly"]: tier.active_tier.archive,
        additionalServices: tier.additional_services,
      };
    }

    if (currentService === serviceType.PAID_RESELLER) {
      return { archive: tier.active_tier.archive, additionalServices: tier.additional_services };
    }

    return null;
  };
  const messages = generateCTAMessage(tier.upgrade_status, requestedLocalStorage);
  const canUpgrade = tier.upgrade_status === "can_upgrade" || tier.upgrade_status === "can_upgrade_to_not_standard";

  const goToResellerForm = () => {
    const url =
      loggedUser.language == "en" ? "https://www.qboxmail.com/resellers/" : "https://www.qboxmail.it/rivenditori";

    window.open(url, "_blank");
  };

  const TierInfoSection = () => (
    <>
      <PricesOverview
        className="mb-8"
        tierMin={tier.tier_min_accounts}
        hasCustomTier={tier.active_tier?.custom_tier}
        current={tier.current_billable_email_accounts}
        currentTier={tier.active_tier.name}
        isPromo={tier.is_promo_tier}
        isAnnual={tier.is_annual}
        isReseller={tier.is_reseller}
      />

      {!tier.is_annual && warningType && (
        <div className="bg-[--yellow-l35] flex flex-col items-center rounded p-5 text-sm mb-8">
          <TranslatedString>{`prices.${warningType}`}</TranslatedString>
        </div>
      )}

      {/* CTA */}
      {tier.standard_tiers && tier.is_reseller && (
        <div className={s.ctaContainer}>
          <div className={s.ctaDesc}>
            <TranslatedString>{messages.desc}</TranslatedString>
          </div>

          {!requestedLocalStorage && (
            <div>
              <Button className={s.btn} color="outline" type="button" onClick={requestUpgrade} disabled={!canUpgrade}>
                {messages.action}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  const ServicesAccess = () => (
    <div className="flex justify-between gap-2 mt-8 text-sm">
      {t("prices.servicesAccess.info")}
      <div>
        <Button onClick={showWHMCSInvoice} color="docs">
          button.servicesAndBilling
        </Button>
      </div>
    </div>
  );

  const getPricesSection = () => {
    if (currentService === serviceType.TRIAL_ENDUSER) {
      return (
        <>
          <div className="mb-5 text-sm font-semibold">{t("prices.monthlyTier.tableTitle")}</div>
          <CustomTierPricesTable prices={tier.monthly_tier} />
          <p className="text-xs">
            <TranslatedString>prices.enduser.monthlyTier.disclaimer</TranslatedString>
          </p>
          <div className="mt-10 mb-5 text-sm font-semibold">{t("prices.annualTier.tableTitle")}</div>
          <CustomTierPricesTable prices={tier.annually_tier} />
          <p className="text-xs">
            <TranslatedString>prices.enduser.annualTier.disclaimer</TranslatedString>
          </p>
        </>
      );
    } else if (currentService === serviceType.TRIAL_RESELLER) {
      return (
        <div className="flex justify-between gap-7">
          <div className="text-sm">
            <TranslatedString>prices.trial.reseller.tierInfo</TranslatedString>
          </div>
          <div>
            <Button onClick={goToResellerForm} color="accent">
              button.fillTheForm
            </Button>
          </div>
        </div>
      );
    } else if (currentService === serviceType.PAID_ENDUSER) {
      return (
        <>
          <TierInfoSection />

          <div className="mb-5 text-sm font-semibold">
            {tier.is_annual ? t("prices.annualTier.tableTitle") : t("prices.monthlyTier.tableTitle")}
          </div>
          <CustomTierPricesTable prices={tier.active_tier} />
          <div className="mt-1 text-xs">
            <TranslatedString>
              {tier.is_annual || tier.active_tier.custom_tier
                ? "prices.enduser.annualTier.disclaimer"
                : "prices.enduser.monthlyTier.disclaimer"}
            </TranslatedString>
          </div>
          <ServicesAccess />
        </>
      );
    } else {
      return (
        <>
          <TierInfoSection />

          {tier.standard_tiers ? (
            <PricesTable standardTiers={tier.standard_tiers} currentTier={tier.active_tier.name} />
          ) : (
            <CustomTierPricesTable prices={tier.active_tier} />
          )}

          <ServicesAccess />
        </>
      );
    }
  };

  const additionalServicesPrices = getAdditionalServicesPrices();

  const getPricesSectionDesc = () => {
    if (
      (currentService === serviceType.PAID_ENDUSER || currentService === serviceType.PAID_RESELLER) &&
      !tier.standard_tiers
    ) {
      return t("prices.custom.sectionSubtitle");
    }

    if (currentService === serviceType.TRIAL_ENDUSER || currentService === serviceType.PAID_ENDUSER) {
      return t("prices.enduser.sectionSubtitle");
    }

    if (currentService === serviceType.TRIAL_RESELLER || currentService === serviceType.PAID_RESELLER) {
      return t("prices.reseller.sectionSubtitle");
    }
  };

  const getPricesSectionTitle = () => {
    return tier.standard_tiers || loggedUser.plan === "trial"
      ? t("prices.sectionTitle")
      : t("prices.custom.sectionTitle");
  };

  const getAdditionalServicesDesc = () => {
    return currentService === serviceType.TRIAL_ENDUSER || currentService === serviceType.PAID_ENDUSER
      ? t("prices.enduser.additionalServices.desc")
      : t("prices.reseller.additionalServices.desc");
  };

  return (
    <div className="flex justify-center p-4 mb-8 overflow-y-auto">
      <div className="flex flex-col sm:w-[32rem] lg:w-[48rem] xl:w-[52rem] pt-4">
        <PageSection
          sectionTitle={getPricesSectionTitle()}
          sectionDesc={getPricesSectionDesc()}
          sectionContent={getPricesSection()}
        />

        <PageSection
          className="mt-12 pt-14 border-t-[1px] border-t-[--bg-d5]"
          sectionTitle={t("prices.additionalServices.title")}
          sectionDesc={getAdditionalServicesDesc()}
          sectionContent={
            <AdditionalServices
              type={currentService}
              prices={additionalServicesPrices}
              isAdmin={loggedUser.type === "Admin"}
              isAnnual={tier.is_annual}
            />
          }
        />
      </div>
    </div>
  );
};

export default view(PricePage);
